import axios from 'axios';
import { config } from '@/config';

const GIFT_API_BASE_URL = config.GIFT_API_BASE_URL;
const GIFT_API_KEY = config.GIFT_API_KEY;

export interface GiftCardDetailsRequest {
  p_CardNumber: string;
  p_MobileNo: string;
  p_AmountToRedeem: number;
}

export interface GiftCardDetailsResponse {
  message: string;
  error_msg?: string;
  cardDetails: {
    name: string;
    mobileNo: string;
    email: string;
    issuedAmount: number;
    usedAmount: number;
    availableBalance: number;
    expiryDate: string;
    qrCode: string;
    issuedCardNumber: string;
    createdDateTime: string;
    isPartial: boolean;
  };
}

export interface GiftCardRedemptionRequest {
  p_IssuedGiftCardNo: string;
  p_RedemedDate: string;
  p_MallId: number;
  p_MerchantCode: string;
  p_AmountToRedeem: number;
  p_OTP: number;
  p_InvoiceNo: string;
  p_UserName: string;
}

export interface GiftCardRedemptionResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface RedemptionReportRequest {
  p_MallId: number;
  p_MerchantCode: string;
  p_Page?: number;
  p_PageSize?: number;
}

export interface RedemptionReportResponse {
  reportResponses?: Array<{
    id: number;
    issuedCardNumber: string | null;
    amountDeducted: number;
    redemedDate: string;
    giftCardStatus: string;
    mobileNo: string;
    mallId: number;
    merchantCode?: string | null;
    merchantName?: string;
    brandCode?: string | null;
    brandName?: string | null;
    invoiceNo?: string | null;
    currency?: string;
    transactionId?: string | null;
  }>;
  totalCount?: number | string;
  error_msg?: string;
  [key: string]: any;
}

export interface AuditLogsRequest {
  userId: number;
  page?: number;
  pageSize?: number;
}

export interface AuditLog {
  id: number;
  user: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  logDate: string;
}

export interface AuditLogsResponse {
  logs?: AuditLog[];
  totalCount?: number;
  [key: string]: any;
}

export interface GetMallDetailsRequest {
  p_UserName: string;
  p_ResourceType: string;
  p_ResourceId: string;
  p_IPAddress: string;
  p_UserAgent: string;
}

export interface GetMallDetailsResponse {
  malldetails?: Array<{
    mallId: number;
    mallName?: string;
    phoneNumber?: string;
    phoneNo?: string;
    address?: string;
    currency?: string | null;
    createdDate?: string;
    updatedDate?: string;
    createdByName?: string;
    updatedByName?: string;
    [key: string]: unknown;
  }>;
  error_msg?: string;
  [key: string]: unknown;
}

export const GET_MALL_DETAILS_PAYLOAD: GetMallDetailsRequest = {
  p_UserName: "Admin",
  p_ResourceType: "Mall",
  p_ResourceId: "1",
  p_IPAddress: "192.168.1.100",
  p_UserAgent: "Mozilla/5.0",
};

export function buildGetMallDetailsPayload(): GetMallDetailsRequest {
  return GET_MALL_DETAILS_PAYLOAD;
}

export class SettingsApi {
  static async getMallDetails(): Promise<GetMallDetailsResponse> {
    const response = await axios.post<GetMallDetailsResponse>(
      `${GIFT_API_BASE_URL}/Settings/GetMallDetails`,
      buildGetMallDetailsPayload(),
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }
}

export interface MemberSearchRequest {
  p_MobileNo: string;
}

export interface MemberSearchResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export interface VerifyMemberOtpRequest {
  p_MobileNo: string;
  p_OTP: string;
}

export interface VerifiedMemberDetail {
  name?: string;
  dob?: string;
  totalTxn?: string | number;
  spend?: number | string;
  pointsBalance?: number | string;
  doa?: string;
  gender?: string;
  city?: string;
  tier?: string | null;
  [key: string]: unknown;
}

export interface VerifyMemberOtpResponse {
  message?: string;
  error_msg?: string;
  memberDetails?: VerifiedMemberDetail[];
  [key: string]: unknown;
}

export interface GetTransactionsByMobileNoRequest {
  p_MobileNo: string;
}

export interface MemberTxnDetail {
  counterId?: string;
  invoiceNo?: string;
  transactionType?: string;
  invoiceAmt?: number | string;
  pointsEarned?: number | string | null;
}

export interface GetTransactionsByMobileNoResponse {
  txnDetails?: MemberTxnDetail[];
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export interface UpdateMemberDetailsRequest {
  p_MobileNo: string;
  p_Name: string;
  p_DOB: string;
  p_DOA: string;
  p_Gender: string;
  p_City: string;
}

export interface UpdateMemberDetailsResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export interface GetMemberDetailsRequest {
  p_MobileNo: string;
}

export interface MemberRuleConfig {
  ruleName?: string;
  expiry?: string;
  couponCode?: string;
}

export interface MemberReward {
  ruleName?: string;
  expiry?: string;
  couponCode?: string;
  couponValue?: string;
}

export interface ActiveCoupon {
  ruleName?: string;
  couponValue?: string;
  minimumPurchase?: string;
  expiry?: string;
}

export interface GetMemberDetailsResponse {
  newMemberDetails?: VerifiedMemberDetail[];
  membersRewards?: MemberReward[];
  activeCoupons?: ActiveCoupon[];
  ruleConfig?: MemberRuleConfig[];
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export class MemberSearchApi {
  static async memberSearch(
    payload: MemberSearchRequest
  ): Promise<MemberSearchResponse> {
    const response = await axios.post<MemberSearchResponse>(
      `${GIFT_API_BASE_URL}/MemberSearch/MemberSearch`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async verifyOtp(
    payload: VerifyMemberOtpRequest
  ): Promise<VerifyMemberOtpResponse> {
    const response = await axios.post<VerifyMemberOtpResponse>(
      `${GIFT_API_BASE_URL}/MemberSearch/VerifyOTP`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async getTransactionsByMobileNo(
    payload: GetTransactionsByMobileNoRequest
  ): Promise<GetTransactionsByMobileNoResponse> {
    const response = await axios.post<GetTransactionsByMobileNoResponse>(
      `${GIFT_API_BASE_URL}/MemberSearch/GetTransactionsByMobileNo`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async updateMemberDetails(
    payload: UpdateMemberDetailsRequest
  ): Promise<UpdateMemberDetailsResponse> {
    const response = await axios.post<UpdateMemberDetailsResponse>(
      `${GIFT_API_BASE_URL}/MemberSearch/UpdateMemberDetails`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }

  static async getMemberDetails(
    payload: GetMemberDetailsRequest
  ): Promise<GetMemberDetailsResponse> {
    const response = await axios.post<GetMemberDetailsResponse>(
      `${GIFT_API_BASE_URL}/MemberSearch/GetMemberDetails`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }
}

export class GiftCardApi {
  static async getGiftCardDetailsByCardNumber(
    payload: GiftCardDetailsRequest
  ): Promise<GiftCardDetailsResponse> {
    try {
      const response = await axios.post<GiftCardDetailsResponse>(
        `${GIFT_API_BASE_URL}/GiftCard/GetGiftCardDetailsByCardNumber`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async giftCardRedemption(
    payload: GiftCardRedemptionRequest
  ): Promise<GiftCardRedemptionResponse> {
    try {
      const response = await axios.post<GiftCardRedemptionResponse>(
        `${GIFT_API_BASE_URL}/GiftCard/GiftCardRedemption`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getRedemptionReportData(
    payload: RedemptionReportRequest
  ): Promise<RedemptionReportResponse> {
    try {
      const response = await axios.post<RedemptionReportResponse>(
        `${GIFT_API_BASE_URL}/Reports/GetRedemptionReportData`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getAllAuditLogs(
    payload: AuditLogsRequest
  ): Promise<AuditLogsResponse> {
    try {
      const response = await axios.post<AuditLogsResponse>(
        `${GIFT_API_BASE_URL}/Audit/GetAllAuditLogs`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async bulkUploadGiftCardIssuance(
    file: File,
    bulkUploadCommandJson = "{}"
  ): Promise<{ message?: string; error_msg?: string; [key: string]: unknown }> {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("BulkUploadCommandJosn", bulkUploadCommandJson);

    const response = await axios.post(
      `${GIFT_API_BASE_URL}/BulkUpload/BulkUploadGiftCardIssuance`,
      formData,
      {
        headers: {
          xapikey: GIFT_API_KEY,
        },
      }
    );
    return response.data;
  }
}

export interface CouponRuleConfig {
  id: number;
  ruleName: string;
  invoiceValueFrom: number;
  invoiceValueTo: number;
  couponType: number;
  couponValue: number;
  issuanceFrom: string;
  issuanceTo: string;
  expiry: string;
  ruleType: number;
  createdBy: number;
  createdDate: string;
  updatedBY: number;
  updatedDate: string;
  RedeemAmountFrom?: number;
  RedeemAmountTo?: number;
  redeemAmountFrom?: number;
  redeemAmountTo?: number;
  isUsed?: boolean;
}

export interface GetAllCouponRuleConfigRequest {
  p_PageNumber: number;
  p_PageSize: number;
}

export interface GetAllCouponRuleConfigResponse {
  couponRuleList: CouponRuleConfig[];
  total_records: number;
  error_msg: string;
}

export interface CreateUpdateCouponRuleConfigRequest {
  p_id?: number;
  p_ruleName: string;
  p_invoiceValueFrom: number;
  p_invoiceValueTo: number;
  p_couponType: number;
  p_couponValue: number;
  p_issuanceFrom: string;
  p_issuanceTo: string;
  p_Expiry: string;
  p_ruleType: number;
  p_createdBy: number;
  p_updatedBy: number;
  p_UsageLimit: number;
  p_RedeemAmountFrom?: number;
  p_RedeemAmountTo?: number;
}

export interface CreateUpdateCouponRuleConfigResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface CouponEarnRedeemTransactionRequest {
  p_invoiceNo: string;
  p_invoiceAmount: number;
  p_mobileNo: string;
  p_isCouponRedeem: boolean;
  p_CouponCode: string;
  p_OTP: string;
  p_OutletId: number;
}

export interface CouponEarnRedeemTransactionResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface TransactionReport {
  mobileNo: string;
  invoiceNo: string;
  invoiceAmount: number;
  transactionType: string | null;
  couponCode: string | null;
  txnDate: string;
  redeemAmount: number;
}

export interface GetTransactionReportRequest {
  p_mobileNo: string | null;
  p_fromDate: string | null;
  p_toDate: string | null;
  p_txnType: string | null;
  p_PageNumber: number;
  p_PageSize: number;
}

export interface GetTransactionReportResponse {
  transactionReportList: TransactionReport[];
  total_records: number;
  error_msg?: string;
}

export interface DeleteCouponRuleRequest {
  p_id: number;
  p_isDelete: boolean;
}

export interface DeleteCouponRuleResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export class CouponApi {
  static async getAllCouponRuleConfig(
    payload: GetAllCouponRuleConfigRequest
  ): Promise<GetAllCouponRuleConfigResponse> {
    try {
      const response = await axios.post<GetAllCouponRuleConfigResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/GetAllCouponRuleConfig`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createUpdateCouponRuleConfig(
    payload: CreateUpdateCouponRuleConfigRequest
  ): Promise<CreateUpdateCouponRuleConfigResponse> {
    try {
      const response = await axios.post<CreateUpdateCouponRuleConfigResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/CreateUpdateCouponRuleConfig`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async couponEarnRedeemTransaction(
    payload: CouponEarnRedeemTransactionRequest
  ): Promise<CouponEarnRedeemTransactionResponse> {
    try {
      const response = await axios.post<CouponEarnRedeemTransactionResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/CouponEarnRedeemTransaction`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getTransactionReport(
    payload: GetTransactionReportRequest
  ): Promise<GetTransactionReportResponse> {
    try {
      const response = await axios.post<GetTransactionReportResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/GetTransactionReport`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async deleteCouponRule(
    payload: DeleteCouponRuleRequest
  ): Promise<DeleteCouponRuleResponse> {
    try {
      const response = await axios.post<DeleteCouponRuleResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/DeleteCouponRule`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async verifyCoupon(
    payload: VerifyCouponRequest
  ): Promise<VerifyCouponResponse> {
    try {
      const response = await axios.post<VerifyCouponResponse>(
        `${GIFT_API_BASE_URL}/CouponRuleConfig/VerifyCoupon`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getCouponAnalyticsReport(): Promise<GetCouponAnalyticsReportResponse> {
    try {
      const response = await axios.post<GetCouponAnalyticsReportResponse>(
        `${GIFT_API_BASE_URL}/CouponAnalytics/GetCouponAnalyticsReport`,
        {},
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export interface VerifyCouponRequest {
  p_mobileNo: string;
  p_couponCode: string;
}

export interface VerifyCouponResponse {
  error_msg?: string;
  [key: string]: any;
}

export interface CouponSummary {
  couponsIssued: number;
  couponsRedeemed: number;
  revenueGenerated: number;
  discountCost: number;
}

export interface PerformingCouponSummary {
  couponName: string;
  type: string;
  issued: number;
  redeemed: number;
  redemption: number;
}

export interface NewReturningCustomersSummary {
  newCustomers: number;
  returningCustomers: number;
}

export interface MonthlySummary {
  month: string;
  issued: number;
  redeemed: number;
  redeemptionRate: number;
}

export interface GetCouponAnalyticsReportResponse {
  couponSummary: CouponSummary[];
  performingCouponSummary?: PerformingCouponSummary[];
  newReturningCustomersSummary?: NewReturningCustomersSummary[];
  monthlySummary?: MonthlySummary[];
  error_msg?: string;
}

// Rule Engine API Interfaces
export interface GetAllRulesRequest {
  id: number;
}

export interface RuleConfiguration {
  ruleId: number;
  title: string;
  isEarnRule: boolean;
  isValidityRule: boolean;
  isRedemptionRule: boolean;
  createdBy: number;
  createddatetime: string;
  updatedBy: number;
  updatedDatetime: string;
}

export interface EarnRule {
  id: number;
  pointsIssuanceType: string;
  minInvoiceAmt: number;
  awardPoints: number;
  ruleId?: number;
}

export interface ValidityRule {
  id: number;
  pointsValidityType: string;
  pointsValidityDays: number;
  ruleId?: number;
}

export interface RedemptionRule {
  id: number;
  burnMinInvoiceAmt: number;
  maxRedemptionPoints: number;
  redemptionCriteria: string;
  earnWhileRedeem: string;
  ruleId?: number;
}

export interface RuleSet {
  ruleConfiguration: RuleConfiguration;
  earnRules: Omit<EarnRule, "ruleId">[];
  validityRules: Omit<ValidityRule, "ruleId">[];
  redemptionRules: Omit<RedemptionRule, "ruleId">[];
}

export interface GetAllRulesApiResponse {
  rules: RuleSet[];
  error_msg?: string;
}

export interface GetAllRulesResponse {
  listConfiguration: RuleConfiguration[];
  listEarnRule: EarnRule[];
  listValidityRule: ValidityRule[];
  listRedemptionRule: RedemptionRule[];
  error_msg?: string;
}

function normalizeGetAllRulesResponse(
  response: GetAllRulesApiResponse
): GetAllRulesResponse {
  const rules = response.rules ?? [];

  return {
    listConfiguration: rules.map((rule) => rule.ruleConfiguration),
    listEarnRule: rules.flatMap((rule) =>
      (rule.earnRules ?? []).map((earnRule) => ({
        ...earnRule,
        ruleId: rule.ruleConfiguration.ruleId,
      }))
    ),
    listValidityRule: rules.flatMap((rule) =>
      (rule.validityRules ?? []).map((validityRule) => ({
        ...validityRule,
        ruleId: rule.ruleConfiguration.ruleId,
      }))
    ),
    listRedemptionRule: rules.flatMap((rule) =>
      (rule.redemptionRules ?? []).map((redemptionRule) => ({
        ...redemptionRule,
        ruleId: rule.ruleConfiguration.ruleId,
      }))
    ),
    error_msg: response.error_msg,
  };
}

export interface CreateUpdateRuleRequest {
  title: string;
  isEarnRule: boolean;
  isValidityRule: boolean;
  isRedemptionRule: boolean;
  createdBy: number;
  updatedBy: number;
  p_id?: number;
  pointsIssuanceType?: string;
  minInvoiceAmt?: number;
  awardPoints?: number;
  pointsValidityType?: string;
  pointsValidityDays?: number;
  burnMinInvoiceAmt?: number;
  maxRedemptionPoints?: number;
  redemptionCriteria?: string;
  earnWhileRedeem?: string;
}

export interface CreateUpdateRuleResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export class RuleEngineApi {
  static async getAllRules(
    payload: GetAllRulesRequest
  ): Promise<GetAllRulesResponse> {
    try {
      const response = await axios.post<GetAllRulesApiResponse>(
        `${GIFT_API_BASE_URL}/PerxiRuleEngine/GetAllRules`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return normalizeGetAllRulesResponse(response.data);
    } catch (error) {
      throw error;
    }
  }

  static async createUpdateRule(
    payload: CreateUpdateRuleRequest
  ): Promise<CreateUpdateRuleResponse> {
    try {
      const response = await axios.post<CreateUpdateRuleResponse>(
        `${GIFT_API_BASE_URL}/PerxiRuleEngine/CreateUpdateRule`,
        payload,
        {
          headers: {
            'xapikey': GIFT_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// SpinAWheel API
export interface WheelSlot {
  slotName: string;
  points: number;
}

export interface CreateUpdateSpinAWheelRequest {
  p_Id: number;
  p_SpinName: string;
  p_StartDate: string;
  p_EndDate: string;
  p_NoOfSlots: number;
  p_SpinPerMember: number;
  p_BonusValidityInDay: number;
  p_NonWinningSlots: number;
  p_ClientId: number;
  p_UseDefault: boolean;
  listWheelSlots: WheelSlot[];
}

export interface CreateUpdateSpinAWheelResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface InsertSpinAWheelTransactionRequest {
  p_MobileNo: string;
  p_OTP: string;
  p_PointsWon: number;
  p_SpinId: number;
}

export interface InsertSpinAWheelTransactionResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface GetAllSpinAwheelConfigRequest {
  // Empty payload
}

export interface SpinWheelConfig {
  id: number;
  spinName: string;
  startDate: string;
  endDate: string;
  noOfSlots: number;
  spinPerMember: number;
  bonusValidityInDay: number;
  nonWinningSlots: number;
  useDefault?: number;
  listWheelSlots?: WheelSlot[];
  [key: string]: any;
}

export interface GetAllSpinAwheelConfigResponse {
  getSpinAwheelConfig?: SpinWheelConfig[];
  error_msg?: string;
  [key: string]: any;
}

export interface GetSpinAwheelConfigByClientIdRequest {
  p_ClientId: number;
}

export interface GetSpinAwheelConfigByClientIdResponse {
  clientSpinAwheelConfig?: SpinWheelConfig;
  error_msg?: string;
  [key: string]: any;
}

export interface GetSpinAwheelReportRequest {
  p_clientId: number;
  p_PageNumber: number;
  p_PageSize: number;
}

export interface SpinAwheelReportDataRow {
  spinId: number;
  spinName: string;
  noOfUsersAttempted: number;
  usersWonReward: number;
  redeemedRewards: number;
  businessGenerated: number;
}

export interface GetSpinAwheelReportResponse {
  getSpinAwheelReport?: SpinAwheelReportDataRow[];
  total_records?: number;
  error_msg?: string;
  [key: string]: any;
}

export interface GetSpinAwheelReportByIdRequest {
  p_spinId: number;
  p_PageNumber: number;
  p_PageSize: number;
}

export interface SpinAwheelReportByIdRow {
  spinName: string;
  userName: string;
  rewards: number;
  redeemed: string; // "Yes" | "No"
  businessGenerated: number;
  issuedDate: string; // ISO
  redeemedDate: string | null;
}

export interface GetSpinAwheelReportByIdResponse {
  getSpinAwheelReportById?: SpinAwheelReportByIdRow[];
  total_records?: number;
  error_msg?: string;
  [key: string]: any;
}

export class SpinAWheelApi {
  static async createUpdateSpinAWheel(
    payload: CreateUpdateSpinAWheelRequest
  ): Promise<CreateUpdateSpinAWheelResponse> {
    try {
      const response = await axios.post<CreateUpdateSpinAWheelResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/CreateUpdateSpinAWheel`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async insertSpinAWheelTransaction(
    payload: InsertSpinAWheelTransactionRequest
  ): Promise<InsertSpinAWheelTransactionResponse> {
    try {
      const response = await axios.post<InsertSpinAWheelTransactionResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/InsertSpinAWheelTransaction`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getAllSpinAwheelConfig(
    payload: GetAllSpinAwheelConfigRequest = {}
  ): Promise<GetAllSpinAwheelConfigResponse> {
    try {
      const response = await axios.post<GetAllSpinAwheelConfigResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/GetAllSpinAwheelConfig`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getSpinAwheelConfigByClientId(
    payload: GetSpinAwheelConfigByClientIdRequest
  ): Promise<GetSpinAwheelConfigByClientIdResponse> {
    try {
      const response = await axios.post<GetSpinAwheelConfigByClientIdResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/GetSpinAwheelConfigByClientId`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getSpinAwheelReportData(
    payload: GetSpinAwheelReportRequest
  ): Promise<GetSpinAwheelReportResponse> {
    try {
      const response = await axios.post<GetSpinAwheelReportResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/GetSpinAWheelReportData`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  static async getSpinAwheelReportById(
    payload: GetSpinAwheelReportByIdRequest
  ): Promise<GetSpinAwheelReportByIdResponse> {
    try {
      const response = await axios.post<GetSpinAwheelReportByIdResponse>(
        `${GIFT_API_BASE_URL}/SpinAWheel/GetSpinAwheelReportById`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Outlet API
export interface OutletResponse {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phone: string;
  email: string;
  createdBy: number;
  createdDateTime: string;
  updatedBy: number;
  updatedDateTime: string;
  isActive: boolean;
}

export interface GetAllOutletsResponse {
  getOutletList: OutletResponse[];
  error_msg: string;
}

export interface CreateUpdateOutletRequest {
  p_Id: number;
  p_Name: string;
  p_Address: string;
  p_City: string;
  p_State: string;
  p_Country: string;
  p_PostalCode: string;
  p_Phone: string;
  p_Email: string;
}

export interface CreateUpdateOutletResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export class OutletApi {
  static async getAllOutlets(): Promise<GetAllOutletsResponse> {
    try {
      const response = await axios.post<GetAllOutletsResponse>(
        `${GIFT_API_BASE_URL}/Outlet/GetAllOutlets`,
        {},
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createUpdateOutlet(
    payload: CreateUpdateOutletRequest
  ): Promise<CreateUpdateOutletResponse> {
    try {
      const response = await axios.post<CreateUpdateOutletResponse>(
        `${GIFT_API_BASE_URL}/Outlet/CreateUpdateOutlet`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Scratch Card API
export interface ScratchCardConfig {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  rewardType: number;
  smallReward: number;
  mediumReward: number;
  bigReward: number;
  useDefault?: number;
  isActive: boolean;
  createdBy: number;
  createdDatetime: string;
  updatedBy: number;
  updatedDatetime: string;
}

export interface GetAllScratchCardConfigRequest {
  [key: string]: any;
}

export interface GetAllScratchCardConfigResponse {
  scratchCard: ScratchCardConfig[];
  error_msg?: string;
}

export interface CreateUpdateScratchCardRequest {
  p_Id?: number;
  p_ClientId: number;
  p_UseDefault: boolean;
  p_Name: string;
  p_StartDate: string;
  p_EndDate: string;
  p_RewardType: number;
  p_SmallReward: number;
  p_MediumReward: number;
  p_BigReward: number;
}

export interface CreateUpdateScratchCardResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface InsertScratchCardTransactionRequest {
  p_MobileNo: string;
  p_OTP: string;
  p_PointsORDiscountWon: number;
  p_ScratchCardId: number;
}

export interface InsertScratchCardTransactionResponse {
  message?: string;
  error_msg?: string;
  [key: string]: any;
}

export interface GetScratchReportRequest {
  p_clientId: number;
  p_PageNumber: number;
  p_PageSize: number;
}

export interface ScratchReportDataRow {
  scratchCardId: number;
  scratchCardName: string;
  noOfUsersAttempted: number;
  usersWonReward: number;
  redeemedRewards: number;
  businessGenerated: number;
}

export interface GetScratchReportResponse {
  scratchReportData?: ScratchReportDataRow[];
  total_records?: number;
  error_msg?: string;
  [key: string]: any;
}

export interface GetScratchCardReportByIdRequest {
  p_scratchCardId: number;
  p_PageNumber: number;
  p_PageSize: number;
}

export interface ScratchCardReportByIdRow {
  scratchCardName: string;
  userName: string;
  rewards: number;
  redeemed: string; // "Yes" | "No"
  businessGenerated: number;
  issuedDate: string; // ISO
  redeemedDate: string | null;
}

export interface GetScratchCardReportByIdResponse {
  getScratchCardReportById?: ScratchCardReportByIdRow[];
  total_records?: number;
  error_msg?: string;
  [key: string]: any;
}

/** Quiz (gamification) — gift API */
export interface QuizOptionPayload {
  text: string;
  is_correct: 0 | 1;
  order_index: number;
}

export interface QuizQuestionPayload {
  question_text: string;
  question_type: string;
  difficulty: string;
  order_index: number;
  options: QuizOptionPayload[];
}

export interface QuizDetailsPayload {
  p_Id: number;
  p_Name: string;
  p_Description: string;
  p_TimeLimitSeconds: number;
  p_MaxScore: number;
  p_PassingScore: number;
  p_CreatedBy: number;
  p_UpdatedBy: number;
}

export interface CreateUpdateQuizWithQuestionsRequest {
  quizDetails: QuizDetailsPayload;
  listQuestions: QuizQuestionPayload[];
}

export interface CreateUpdateQuizWithQuestionsResponse {
  error_msg?: string;
  quizDetails?: QuizDetailsPayload & { p_Id?: number };
  [key: string]: unknown;
}

/** Body for `Quiz/GetAllQuizzes` (extend when API adds filters). */
export type GetAllQuizzesRequest = Record<string, unknown>;

/**
 * Response shape from `POST .../Quiz/GetAllQuizzes`
 * @see getAllQuiz — list of quizzes with nested questions and MCQ options
 */
export interface QuizListOption {
  option_id: number;
  text: string;
  /** 0 = incorrect, 1 = correct */
  is_correct: 0 | 1;
  order_index: number;
}

export interface QuizListQuestion {
  question_id: number;
  question_text: string;
  question_type: string;
  difficulty: string;
  order_index: number;
  options: QuizListOption[];
}

export interface QuizListItem {
  id: number;
  name: string;
  description: string;
  time_limit_seconds: number;
  max_score: number;
  passing_score: number;
  is_active: boolean;
  created_by: number;
  created_datetime: string;
  questions: QuizListQuestion[];
}

export interface GetAllQuizzesResponse {
  getAllQuiz: QuizListItem[];
  error_msg: string;
}

export interface GetQuizByQuizIdRequest {
  p_QuizId: number;
  p_MobileNo: number;
}

export interface GetQuizByQuizIdResponse {
  error_msg?: string;
  message?: string;
  [key: string]: unknown;
}

export class QuizApi {
  static async getAllQuizzes(
    payload: GetAllQuizzesRequest = {}
  ): Promise<GetAllQuizzesResponse> {
    const response = await axios.post<GetAllQuizzesResponse>(
      `${GIFT_API_BASE_URL}/Quiz/GetAllQuizzes`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }

  static async getQuizByQuizId(
    payload: GetQuizByQuizIdRequest
  ): Promise<GetQuizByQuizIdResponse> {
    const response = await axios.post<GetQuizByQuizIdResponse>(
      `${GIFT_API_BASE_URL}/Quiz/GetQuizByQuizId`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }

  static async createUpdateQuizWithQuestions(
    payload: CreateUpdateQuizWithQuestionsRequest
  ): Promise<CreateUpdateQuizWithQuestionsResponse> {
    const response = await axios.post<CreateUpdateQuizWithQuestionsResponse>(
      `${GIFT_API_BASE_URL}/Quiz/CreateUpdateQuizWithQuestions`,
      payload,
      {
        headers: {
          xapikey: GIFT_API_KEY,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }
}

export class ScratchCardApi {
  static async getAllScratchCardConfig(
    payload: GetAllScratchCardConfigRequest = {}
  ): Promise<GetAllScratchCardConfigResponse> {
    try {
      const response = await axios.post<GetAllScratchCardConfigResponse>(
        `${GIFT_API_BASE_URL}/ScratchCardConfig/GetAllScratchCardConfig`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createUpdateScratchCard(
    payload: CreateUpdateScratchCardRequest
  ): Promise<CreateUpdateScratchCardResponse> {
    try {
      const response = await axios.post<CreateUpdateScratchCardResponse>(
        `${GIFT_API_BASE_URL}/ScratchCardConfig/CreateUpdateScratchCardConfig`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async insertScratchCardTransaction(
    payload: InsertScratchCardTransactionRequest
  ): Promise<InsertScratchCardTransactionResponse> {
    try {
      const response = await axios.post<InsertScratchCardTransactionResponse>(
        `${GIFT_API_BASE_URL}/ScratchCardConfig/InsertScratchCardTransaction`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getScratchReportData(
    payload: GetScratchReportRequest
  ): Promise<GetScratchReportResponse> {
    try {
      const response = await axios.post<GetScratchReportResponse>(
        `${GIFT_API_BASE_URL}/ScratchCardConfig/GetScratchReportData`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getScratchCardReportById(
    payload: GetScratchCardReportByIdRequest
  ): Promise<GetScratchCardReportByIdResponse> {
    try {
      const response = await axios.post<GetScratchCardReportByIdResponse>(
        `${GIFT_API_BASE_URL}/ScratchCardConfig/GetScratchCardReportById`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export interface GameBoxPrize {
  id: number;
  isActive: number;
  prizeCode: string;
  prizeName: string;
  prizeType: string;
  prizeValue: number;
  perUserLimit: number;
  winProbability: number;
  createdDateTime: string;
  updatedDateTime: string;
}

export interface GameBoxConfig {
  id: number;
  title: string;
  imageUrl: string;
  guaranteedWinFlag: boolean;
  showNearMiss: boolean;
  displayText: string;
  termsText: string;
  createdBy: number;
  createdDateTime: string;
  updatedBy: number;
  updatedDateTime: string;
  prizes: GameBoxPrize[];
}

export interface GetAllGameBoxConfigurationRequest {
  p_PageNumber: number;
  p_PageSize: number;
}

export interface GetAllGameBoxConfigurationResponse {
  getAllGameBoxConfig?: GameBoxConfig[];
  total_records?: number;
  error_msg?: string;
}

export interface InsertUpdateGameBoxGameBox {
  p_Id: number;
  p_Title: string;
  p_ImageUrl: string;
  p_GuaranteedWinFlag: boolean;
  p_ShowNearMiss: boolean;
  p_DisplayText: string;
  p_TermsText: string;
  p_CreatedBy: number;
  p_UpdatedBy: number;
  p_UseDefault: boolean;
  p_ClientId: number;
}

export interface InsertUpdateGameBoxPrize {
  PrizeCode: string;
  PrizeName: string;
  PrizeType: string;
  PrizeValue: number;
  PerUserLimit: number;
  IsActive: boolean;
  WinProbability: number;
}

export interface InsertUpdateGameBoxCommonJson {
  gameBox: InsertUpdateGameBoxGameBox;
  ListPrizes: InsertUpdateGameBoxPrize[];
}

export interface InsertUpdateGameBoxConfigRequest {
  file?: File;
  commonJson: InsertUpdateGameBoxCommonJson;
}

export interface InsertUpdateGameBoxConfigResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export class GameBoxApi {
  static async getAllGameBoxConfiguration(
    payload: GetAllGameBoxConfigurationRequest
  ): Promise<GetAllGameBoxConfigurationResponse> {
    try {
      const response = await axios.post<GetAllGameBoxConfigurationResponse>(
        `${GIFT_API_BASE_URL}/GameBoxConfig/GetAllGameBoxConfiguration`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async insertUpdateGameBoxConfig(
    payload: InsertUpdateGameBoxConfigRequest
  ): Promise<InsertUpdateGameBoxConfigResponse> {
    try {
      const formData = new FormData();
      if (payload.file) {
        formData.append("File", payload.file);
      }
      formData.append("CommonJosn", JSON.stringify(payload.commonJson));

      const response = await axios.post<InsertUpdateGameBoxConfigResponse>(
        `${GIFT_API_BASE_URL}/GameBoxConfig/InsertUpdateGameBoxConfig`,
        formData,
        {
          headers: {
            xapikey: GIFT_API_KEY,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Perk Point — Insert Transaction
export interface TransactionProductDetail {
  productCode: string;
  productName: string;
  productAmount: string;
  productQuantity: number;
  txnDateTime: string;
}

export interface InsertTransactionDetails {
  p_TransactionType: string;
  p_CounterId: number;
  p_MobileNo: string;
  p_InvoiceNo: string;
  p_InvoiceAmount: number;
  p_CustomerName: string;
  p_BurnPoints: number;
}

export interface InsertTransactionRequest {
  transactionDetails: InsertTransactionDetails;
  listProductDetails: TransactionProductDetail[];
}

export interface InsertTransactionResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export interface GetAllTransactionsRequest {
  p_PageNumber: number;
  p_PageSize: number;
}

export interface TransactionReportItem {
  mobileNo: string;
  invoiceNo: string;
  invoiceAmt: number;
  transactionType: string;
  transactedDate: string;
  counterId: string;
}

export interface GetAllTransactionsResponse {
  allTransactions?: TransactionReportItem[];
  total_records?: number;
  error_msg?: string;
}

export class PerkPointTransactionApi {
  static async insertTransaction(
    payload: InsertTransactionRequest
  ): Promise<InsertTransactionResponse> {
    try {
      const response = await axios.post<InsertTransactionResponse>(
        `${GIFT_API_BASE_URL}/Transaction/InsertTransaction`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getAllTransactions(
    payload: GetAllTransactionsRequest
  ): Promise<GetAllTransactionsResponse> {
    try {
      const response = await axios.post<GetAllTransactionsResponse>(
        `${GIFT_API_BASE_URL}/Transaction/GetAllTransactions`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Perk Point — Reports
export interface PagedReportRequest {
  p_PageNumber: number;
  p_PageSize: number;
}

export interface CustWiseTotalEarnBurnReportItem {
  mobileNo: string;
  name: string | null;
  totalTxnCount: number;
  pointsEarned: number;
  pointsRedeemed: number;
  pointsBalance: number;
  totalSpend: number;
}

export interface CustWiseTotalEarnBurnReportResponse {
  reportResponse?: CustWiseTotalEarnBurnReportItem[];
  total_records?: number;
  error_msg?: string;
}

export interface PointsExpiryReportItem {
  mobileNo: string;
  name: string | null;
  outletName: string | null;
  expiredPoints: number | null;
  expiredDate: string | null;
}

export interface PointsExpiryReportResponse {
  expiryResponse?: PointsExpiryReportItem[];
  total_records?: number;
  error_msg?: string;
}

export class PerkPointReportsApi {
  static async getCustWiseTotalEarnBurnReport(
    payload: PagedReportRequest
  ): Promise<CustWiseTotalEarnBurnReportResponse> {
    try {
      const response = await axios.post<CustWiseTotalEarnBurnReportResponse>(
        `${GIFT_API_BASE_URL}/Reports/GetCustWiseTotalEarnBurnReport`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getPointsExpiryReport(
    payload: PagedReportRequest
  ): Promise<PointsExpiryReportResponse> {
    try {
      const response = await axios.post<PointsExpiryReportResponse>(
        `${GIFT_API_BASE_URL}/Reports/GetPointsExpiryReport`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// ─── Slot Machine Configuration ─────────────────────────────────────────────

export interface SlotRule {
  id: number;
  slotId: number;
  symbolId: number;
  occurance: number;
  pointToWin: number;
  createdDateTime?: string;
}

export interface SlotConfiguration {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  winningProbability: number;
  createdBy: number;
  createdDateTime?: string;
  updatedBy?: number;
  updatedDateTime?: string | null;
  slotRules: SlotRule[];
}

export interface GetAllSlotConfigurationRequest {
  p_PageNumber?: number;
  p_PageSize?: number;
}

export interface GetAllSlotConfigurationResponse {
  getAllSlotConfig?: SlotConfiguration[];
  total_records?: number;
  error_msg?: string;
}

export interface InsertUpdateSlotConfigurationPayload {
  slotConfiguration: {
    p_Id?: number;
    p_Name: string;
    p_StartDate: string;
    p_EndDate: string;
    p_WinningProbability: number;
    p_CreatedBy: number;
    p_UpdatedBy: number;
  };
  listSlotRules: Array<{
    symbolId: number;
    occurance: number;
    pointToWin: number;
  }>;
}

export interface InsertUpdateSlotConfigurationResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export class SlotConfigurationApi {
  static async getAllSlotConfiguration(
    payload: GetAllSlotConfigurationRequest = {}
  ): Promise<GetAllSlotConfigurationResponse> {
    try {
      const response = await axios.post<GetAllSlotConfigurationResponse>(
        `${GIFT_API_BASE_URL}/SlotConfiguration/GetAllSlotConfiguration`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async insertUpdateSlotConfiguration(
    payload: InsertUpdateSlotConfigurationPayload
  ): Promise<InsertUpdateSlotConfigurationResponse> {
    try {
      const response = await axios.post<InsertUpdateSlotConfigurationResponse>(
        `${GIFT_API_BASE_URL}/SlotConfiguration/InsertUpdateSlotConfiguration`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Tier API
export interface GetAllTiersRequest {
  p_PageNumber: number;
  p_PageSize: number;
}

export interface TierItem {
  id: number;
  tierName: string;
  tierType: string;
  createdBy: number;
  createdDate: string;
  updatedBy: number;
  updatedDate: string | null;
  clientId: string;
  txHash: string | null;
  fromValue: number;
  toValue: number;
  percentage: number;
  explorerUrl: string | null;
}

export interface GetAllTiersResponse {
  getAllTiersList?: TierItem[];
  total_records?: number | string;
  error_msg?: string;
}

export interface InsertUpdateTiersRequest {
  p_Id?: number;
  p_TierName: string;
  p_TierType: string;
  p_FromValue: number;
  p_ToValue: number;
  p_Percentage: number;
  p_CreatedBy?: number;
  p_UpdatedBy?: number;
  p_ClientId: string;
}

export interface InsertUpdateTiersResponse {
  message?: string;
  error_msg?: string;
  [key: string]: unknown;
}

export class TierApi {
  static async getAllTiers(
    payload: GetAllTiersRequest
  ): Promise<GetAllTiersResponse> {
    try {
      const response = await axios.post<GetAllTiersResponse>(
        `${GIFT_API_BASE_URL}/Tier/GetAllTiers`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async insertUpdateTiers(
    payload: InsertUpdateTiersRequest
  ): Promise<InsertUpdateTiersResponse> {
    try {
      const response = await axios.post<InsertUpdateTiersResponse>(
        `${GIFT_API_BASE_URL}/Tier/InsertUpdateTiers`,
        payload,
        {
          headers: {
            xapikey: GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}
